import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { resetPassword } from '../api/api';
import { ArrowLeft, Lock, CheckCircle, Eye, EyeOff } from 'lucide-react';

const Logo = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 7.5L12 4L20 7.5L12 11L4 7.5Z" fill="currentColor" />
    <path d="M4 11.5L12 15L20 11.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 15.5L12 19L20 15.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [repeatedNewPassword, setRepeatedNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError('Token de recuperação não encontrado. Por favor, solicite um novo link.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== repeatedNewPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    if (newPassword.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres.');
      return;
    }

    if (!token) {
      setError('Token de recuperação não encontrado.');
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword(token, newPassword, repeatedNewPassword);
      setSuccess('Senha redefinida com sucesso! Redirecionando para o login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      setError('Erro ao redefinir senha. O token pode estar expirado ou inválido. Por favor, solicite um novo link.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex flex-col items-center justify-center p-4 bg-grid">
      <div className="absolute top-8 left-8">
        <Link 
          to="/login"
          className="flex items-center gap-2 text-gray-500 hover:text-purple-600 font-bold transition-all group"
        >
          <div className="w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center group-hover:bg-purple-50 group-hover:border-purple-100 shadow-sm transition-all">
            <ArrowLeft size={18} />
          </div>
          <span>Voltar ao login</span>
        </Link>
      </div>

      <div className="w-full max-w-md animate-fade-in">
        <div className="flex flex-col items-center text-center mb-10">
          <div className="flex items-center text-black mb-4">
            <Logo size={48} />
            <span className="font-display text-4xl ml-4 tracking-tighter">SORA</span>
          </div>
          <h1 className="text-3xl font-black mb-2 font-hero">
            Redefinir Senha
          </h1>
          <p className="text-gray-500 font-medium">
            Digite sua nova senha abaixo.
          </p>
        </div>

        <div className="bg-white p-8 md:p-10 rounded-[3rem] shadow-xl border border-gray-100">
          {success && (
            <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-xl">
              <p className="text-green-800 font-medium text-sm">{success}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl">
              <p className="text-red-800 font-medium text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                Nova Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <Lock size={18} />
                </div>
                <input 
                  type={showPassword ? "text" : "password"} 
                  placeholder="••••••••" 
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading || !token}
                  minLength={8}
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-purple-200 rounded-2xl py-4 pl-12 pr-12 outline-none transition-all font-medium disabled:opacity-50"
                />
                <button 
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                Confirme sua Nova Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <Lock size={18} />
                </div>
                <input 
                  type={showPassword ? "text" : "password"} 
                  placeholder="••••••••" 
                  required
                  value={repeatedNewPassword}
                  onChange={(e) => setRepeatedNewPassword(e.target.value)}
                  disabled={isLoading || !token}
                  minLength={8}
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-purple-200 rounded-2xl py-4 pl-12 pr-4 outline-none transition-all font-medium disabled:opacity-50"
                />
              </div>
            </div>

            <button 
              type="submit"
              disabled={isLoading || !token}
              className={`w-full bg-black text-white font-display py-5 rounded-2xl text-lg shadow-xl shadow-gray-200 transition-all active:scale-95 flex items-center justify-center gap-3 ${isLoading || !token ? 'opacity-70 cursor-wait' : 'hover:bg-gray-800'}`}
            >
              {isLoading ? (
                <div className="w-6 h-6 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <CheckCircle size={20} />
                  Redefinir Senha
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-50 text-center">
            <p className="text-gray-500 font-medium">
              <Link 
                to="/password/forgot" 
                className="text-purple-600 font-bold hover:underline"
              >
                Solicitar novo link de recuperação
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      <p className="mt-12 text-gray-400 text-sm font-medium">
        © 2025 Sôra — Todos os direitos reservados.
      </p>
    </div>
  );
};

export default ResetPassword;
